import Phaser from 'phaser';
import { Client, Room } from 'colyseus.js';
import GameScene from './scenes/GameScene.js';

const WEBSOCKET_URL = (import.meta as any).env?.VITE_WEBSOCKET_URL || 'ws://localhost:2567';

// Player color CSS values (match server-side PLAYER_COLOR_CHOICES)
const PLAYER_COLORS = ['#ff6b6b', '#4ecdc4', '#a78bfa', '#fbbf24'];

// Difficulty levels
enum Difficulty {
  SETTLER = 'settler',
  SKIRMISHER = 'skirmisher',
  WARLORD = 'warlord',
  ANARCHY = 'anarchy'
}

const DIFFICULTY_CONFIGS: Record<Difficulty, { name: string; description: string; gracePeriodMs: number; lives: number }> = {
  [Difficulty.SETTLER]: { name: 'Settler', description: 'Beginner: 250ms grace period, 3 lives, slower trails', gracePeriodMs: 250, lives: 3 },
  [Difficulty.SKIRMISHER]: { name: 'Skirmisher', description: 'Intermediate: 100ms grace period, 2 lives', gracePeriodMs: 100, lives: 2 },
  [Difficulty.WARLORD]: { name: 'Warlord', description: 'Expert: Instant death, 1 life', gracePeriodMs: 0, lives: 1 },
  [Difficulty.ANARCHY]: { name: 'Anarchy', description: 'Impossible: Warlord rules + territorial decay + void pulses', gracePeriodMs: 0, lives: 1 }
};

// localStorage keys
const LS_COLOR = 'linklock_color';
const LS_EMOJI = 'linklock_emoji';
const LS_MUTED = 'linklock_muted';
const LS_DIFFICULTY = 'linklock_difficulty';
const LS_EXPERT_CLEARED = 'linklock_expert_cleared';

// Attract mode config
const ATTRACT_IDLE_TIMEOUT = 45000; // 45 seconds
const ATTRACT_DURATION = 60000; // 60 seconds
const ATTRACT_BOT_DELAY = 300; // 300ms reaction delay
const ATTRACT_MISS_RATE = 0.15; // 15% miss rate
const GRID_WIDTH = 30;
const GRID_HEIGHT = 22;

type Screen = 'landing' | 'connecting' | 'lobby' | 'hud' | 'post-match' | 'attract' | 'difficulty-select';

class LinkLockGame {
  private client: Client;
  private room?: Room;
  localPlayerId: number = -1;
  private isHost = false;

  // Customization state
  private localColorChoice: number = 0;
  private localEmoji: string = '🐱';

  // Rematch state
  private rematchP1Ready = false;
  private rematchP2Ready = false;
  private hasClickedRematch = false;

  // Track which screen is active for context-appropriate messages
  private currentScreen: Screen = 'landing';

  // Mute state
  private isMuted = false;
  private preAttractMuteState = false;

  // Attract mode state
  private attractModeActive = false;
  private attractIdleTimer?: number;
  private attractSessionTimer?: number;
  private botInterval?: number;
  private bot1: BotAI | null = null;
  private bot2: BotAI | null = null;

  // Difficulty state
  private selectedDifficulty: Difficulty = Difficulty.SKIRMISHER;
  private isAnarchyUnlocked = false;

  constructor() {
    this.client = new Client(WEBSOCKET_URL);
    this.loadCustomization();
    this.loadMuteState();
    this.loadDifficultyState();
    this.initPhaser(); // Eagerly init so canvas is always in DOM
    this.initUI();
    this.initMuteButton();
    this.initDifficultyUI();
    this.start();
  }

  // ── Mute functionality ─────────────────────────────────────────

  private loadMuteState() {
    const muted = localStorage.getItem(LS_MUTED);
    this.isMuted = muted === 'true';
    this.updateMuteButtonUI();
  }

  private saveMuteState() {
    localStorage.setItem(LS_MUTED, String(this.isMuted));
  }

  private initMuteButton() {
    const muteBtn = document.getElementById('mute-btn') as HTMLButtonElement;
    muteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMute();
    });
    this.updateMuteButtonUI();
  }

  private toggleMute() {
    this.isMuted = !this.isMuted;
    this.saveMuteState();
    this.updateMuteButtonUI();
  }

  private updateMuteButtonUI() {
    const muteBtn = document.getElementById('mute-btn') as HTMLButtonElement;
    muteBtn.textContent = this.isMuted ? '🔇' : '🔊';
  }

  // ── Attract mode ────────────────────────────────────────────────

  private startAttractIdleTimer() {
    this.clearAttractTimers();
    
    // Only start attract mode on landing screen
    if (this.currentScreen !== 'landing') return;
    
    this.attractIdleTimer = window.setTimeout(() => {
      if (this.currentScreen === 'landing') {
        this.startAttractMode();
      }
    }, ATTRACT_IDLE_TIMEOUT);
  }

  private resetAttractIdleTimer() {
    if (this.attractModeActive) {
      this.stopAttractMode();
    }
    this.startAttractIdleTimer();
  }

  private startAttractMode() {
    if (this.attractModeActive) return;
    
    console.log('Starting attract mode');
    this.attractModeActive = true;
    this.currentScreen = 'attract';
    
    // Show HUD for attract mode (needed to display scores)
    document.getElementById('hud-overlay')?.classList.remove('hidden');
    
    // Auto-mute on attract start
    this.preAttractMuteState = this.isMuted;
    if (!this.isMuted) {
      this.isMuted = true;
      this.updateMuteButtonUI();
    }

    // Initialize bots
    this.bot1 = new BotAI(0, 0xff6b6b);
    this.bot2 = new BotAI(1, 0x4ecdc4);

    // Reset timer display
    document.getElementById('timer')!.textContent = '1:00';

    // Start bot loop
    this.botInterval = window.setInterval(() => {
      this.updateBots();
      this.updateAttractTimer();
    }, 100);

    // Set session timeout (60 seconds max)
    this.attractSessionTimer = window.setTimeout(() => {
      this.stopAttractMode();
    }, ATTRACT_DURATION);

    // Tell game scene to start attract mode rendering
    if (window.gameScene) {
      (window.gameScene as any).startAttractMode();
    }
  }

  private attractTimeRemaining = ATTRACT_DURATION;

  private updateAttractTimer() {
    this.attractTimeRemaining -= 100;
    const seconds = Math.max(0, Math.floor(this.attractTimeRemaining / 1000));
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    document.getElementById('timer')!.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private stopAttractMode() {
    if (!this.attractModeActive) return;

    console.log('Stopping attract mode');
    this.attractModeActive = false;
    this.clearAttractTimers();
    this.attractTimeRemaining = ATTRACT_DURATION;

    // Hide HUD after attract mode
    document.getElementById('hud-overlay')?.classList.add('hidden');

    // Clean up bots
    if (this.botInterval) {
      clearInterval(this.botInterval);
      this.botInterval = undefined;
    }
    this.bot1 = null;
    this.bot2 = null;

    // Restore mute state
    this.isMuted = this.preAttractMuteState;
    this.updateMuteButtonUI();

    // Return to landing
    this.currentScreen = 'landing';
    
    // Stop game scene attract mode rendering
    if (window.gameScene) {
      (window.gameScene as any).stopAttractMode();
    }
    
    // Restart idle timer
    this.startAttractIdleTimer();
  }

  private clearAttractTimers() {
    if (this.attractIdleTimer) {
      clearTimeout(this.attractIdleTimer);
      this.attractIdleTimer = undefined;
    }
    if (this.attractSessionTimer) {
      clearTimeout(this.attractSessionTimer);
      this.attractSessionTimer = undefined;
    }
  }

  private updateBots() {
    if (!this.bot1 || !this.bot2) return;

    // Update bot 1 (try to close loops and claim territory)
    this.bot1.update(this.bot2);
    
    // Update bot 2
    this.bot2.update(this.bot1);

    // Check if either bot died (end attract mode)
    if (!this.bot1.isAlive || !this.bot2.isAlive) {
      this.stopAttractMode();
      return;
    }

    // Update game scene with bot positions
    if (window.gameScene) {
      (window.gameScene as any).updateBotState(this.bot1.getState(), this.bot2.getState());
      // Also update the grid for rendering
      const combinedGrid = this.mergeBotGrids();
      (window.gameScene as any).setBotGrid(combinedGrid);
    }

    // Update HUD scores
    this.updateScore(0, this.bot1.territory);
    this.updateScore(1, this.bot2.territory);
  }

  private mergeBotGrids(): number[][] {
    const grid: number[][] = [];
    for (let y = 0; y < GRID_HEIGHT; y++) {
      const row: number[] = [];
      for (let x = 0; x < GRID_WIDTH; x++) {
        row.push(0); // EMPTY
      }
      grid.push(row);
    }

    // Merge bot1 territory/trails
    if (this.bot1) {
      for (const pos of this.bot1.trail) {
        if (pos.x >= 0 && pos.x < GRID_WIDTH && pos.y >= 0 && pos.y < GRID_HEIGHT) {
          grid[pos.y][pos.x] = 3; // TRAIL_P1
        }
      }
    }

    // Merge bot2 territory/trails
    if (this.bot2) {
      for (const pos of this.bot2.trail) {
        if (pos.x >= 0 && pos.x < GRID_WIDTH && pos.y >= 0 && pos.y < GRID_HEIGHT) {
          grid[pos.y][pos.x] = 4; // TRAIL_P2
        }
      }
    }

    return grid;
  }

  // ── Customization ─────────────────────────────────────────────

  private loadCustomization() {
    const color = localStorage.getItem(LS_COLOR);
    const emoji = localStorage.getItem(LS_EMOJI);

    this.localColorChoice = color !== null ? parseInt(color, 10) : 0;
    this.localEmoji = emoji || '🐱';

    if (this.localColorChoice < 0 || this.localColorChoice > 3) {
      this.localColorChoice = 0;
    }
  }

  private saveCustomization() {
    localStorage.setItem(LS_COLOR, String(this.localColorChoice));
    localStorage.setItem(LS_EMOJI, this.localEmoji);
  }

  private applyCustomizationToUI() {
    document.querySelectorAll('.color-opt').forEach(el => {
      const idx = parseInt((el as HTMLElement).dataset.index || '0', 10);
      el.classList.toggle('selected', idx === this.localColorChoice);
    });
    document.querySelectorAll('.emoji-opt').forEach(el => {
      const e = (el as HTMLElement).dataset.emoji || '';
      el.classList.toggle('selected', e === this.localEmoji);
    });
  }

  // ── Difficulty ────────────────────────────────────────────────

  private loadDifficultyState() {
    const savedDifficulty = localStorage.getItem(LS_DIFFICULTY) as Difficulty | null;
    const expertCleared = localStorage.getItem(LS_EXPERT_CLEARED);
    
    this.isAnarchyUnlocked = expertCleared === 'true';
    
    // Validate saved difficulty
    if (savedDifficulty && Object.values(Difficulty).includes(savedDifficulty)) {
      // Don't allow anarchy if not unlocked
      if (savedDifficulty === Difficulty.ANARCHY && !this.isAnarchyUnlocked) {
        this.selectedDifficulty = Difficulty.SKIRMISHER;
      } else {
        this.selectedDifficulty = savedDifficulty;
      }
    } else {
      this.selectedDifficulty = Difficulty.SKIRMISHER;
    }
  }

  private saveDifficulty() {
    localStorage.setItem(LS_DIFFICULTY, this.selectedDifficulty);
  }

  private unlockAnarchy() {
    this.isAnarchyUnlocked = true;
    localStorage.setItem(LS_EXPERT_CLEARED, 'true');
    this.updateDifficultyUI();
  }

  private setDifficulty(difficulty: Difficulty) {
    // Prevent setting anarchy if not unlocked
    if (difficulty === Difficulty.ANARCHY && !this.isAnarchyUnlocked) {
      return;
    }
    
    this.selectedDifficulty = difficulty;
    this.saveDifficulty();
    this.updateDifficultyUI();
  }

  private initDifficultyUI() {
    // Difficulty selector on landing screen
    const difficultyContainer = document.getElementById('difficulty-selector');
    if (!difficultyContainer) return;

    // Create difficulty buttons
    const difficulties = [Difficulty.SETTLER, Difficulty.SKIRMISHER, Difficulty.WARLORD, Difficulty.ANARCHY];
    
    difficulties.forEach(diff => {
      const btn = document.createElement('button');
      btn.className = 'difficulty-btn';
      btn.dataset.difficulty = diff;
      
      const config = DIFFICULTY_CONFIGS[diff];
      const isLocked = diff === Difficulty.ANARCHY && !this.isAnarchyUnlocked;
      
      btn.innerHTML = `
        <span class="difficulty-name">${config.name} ${isLocked ? '🔒' : ''}</span>
        <span class="difficulty-desc">${config.description}</span>
      `;
      
      if (isLocked) {
        btn.disabled = true;
        btn.classList.add('locked');
      } else {
        btn.addEventListener('click', () => this.setDifficulty(diff));
      }
      
      difficultyContainer.appendChild(btn);
    });

    this.updateDifficultyUI();
  }

  private updateDifficultyUI() {
    document.querySelectorAll('.difficulty-btn').forEach(el => {
      const btn = el as HTMLButtonElement;
      const diff = btn.dataset.difficulty as Difficulty;
      btn.classList.toggle('selected', diff === this.selectedDifficulty);
    });
  }

  // ── UI wiring ──────────────────────────────────────────────────

  private initUI() {
    // Attract mode: reset idle timer on any user input
    const resetAttractTimer = () => this.resetAttractIdleTimer();
    document.addEventListener('click', resetAttractTimer);
    document.addEventListener('keydown', resetAttractTimer);

    // Landing: join by code input (create room happens automatically for hosts)
    const joinBtn = document.getElementById('join-room-btn') as HTMLButtonElement;
    const joinInput = document.getElementById('join-code-input') as HTMLInputElement;

    joinBtn.addEventListener('click', () => this.onJoinRoom());
    joinInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.onJoinRoom();
    });

    // "Create Room" button (shown on landing page, shows difficulty selector)
    const createBtn = document.getElementById('create-room-btn') as HTMLButtonElement;
    createBtn.addEventListener('click', () => {
      this.showDifficultySelector();
    });

    // Copy invite link
    document.getElementById('copy-code-btn')?.addEventListener('click', () => this.copyInviteLink());

    // Start match button
    document.getElementById('start-match-btn')?.addEventListener('click', () => {
      if (this.room && this.isHost) {
        this.room.send('start_match', {});
      }
    });

    // Color picker
    document.getElementById('color-picker')!.addEventListener('click', (e) => {
      const opt = (e.target as HTMLElement).closest('.color-opt') as HTMLElement | null;
      if (!opt) return;
      const idx = parseInt(opt.dataset.index || '0', 10);
      this.localColorChoice = idx;
      this.saveCustomization();
      this.applyCustomizationToUI();
      this.updateMyPlayerCard();
      if (this.room) {
        this.room.send('update_customization', { colorChoice: idx, emoji: this.localEmoji });
      }
    });

    // Emoji picker
    document.getElementById('emoji-picker')!.addEventListener('click', (e) => {
      const opt = (e.target as HTMLElement).closest('.emoji-opt') as HTMLElement | null;
      if (!opt) return;
      const emoji = opt.dataset.emoji || '🐱';
      this.localEmoji = emoji;
      this.saveCustomization();
      this.applyCustomizationToUI();
      this.updateMyPlayerCard();
      if (this.room) {
        this.room.send('update_customization', { colorChoice: this.localColorChoice, emoji });
      }
    });

    // Play again
    document.getElementById('play-again-btn')?.addEventListener('click', () => this.onPlayAgain());

    this.applyCustomizationToUI();
  }

  /** Entry point: check URL and route to appropriate flow. */
  private start() {
    const urlParams = new URLSearchParams(window.location.search);
    const roomCode = urlParams.get('room');

    if (roomCode) {
      // Second player joining via shared link - use host's difficulty
      this.showScreen('connecting');
      document.getElementById('connecting-msg')!.textContent = `Joining room ${roomCode}…`;
      this.joinRoom(roomCode);
    } else {
      // Host: show landing and auto-create a room with stored difficulty
      this.showScreen('landing');
      this.createRoom();
    }

    // Start attract mode idle timer
    this.startAttractIdleTimer();
  }

  private showDifficultySelector() {
    // Show difficulty selector overlay
    const selector = document.getElementById('difficulty-select-screen');
    if (selector) {
      selector.classList.remove('hidden');
    }

    // Update unlock hint visibility
    const unlockHint = document.getElementById('unlock-hint');
    if (unlockHint) {
      unlockHint.style.display = this.isAnarchyUnlocked ? 'none' : 'block';
    }

    // Setup the confirm button
    const confirmBtn = document.getElementById('confirm-difficulty-btn') as HTMLButtonElement;
    if (confirmBtn) {
      confirmBtn.onclick = async () => {
        this.hideDifficultySelector();
        // Leave any existing auto-created room before creating a new one with new difficulty
        if (this.room) {
          await this.room.leave();
          this.room = undefined;
          window.history.replaceState({}, '', '/');
        }
        this.showScreen('connecting');
        document.getElementById('connecting-msg')!.textContent = 'Creating room…';
        this.createRoom();
      };
    }
  }

  private hideDifficultySelector() {
    this.showScreen('landing');
  }

  // ── Room actions ───────────────────────────────────────────────

  private async onJoinRoom() {
    const input = document.getElementById('join-code-input') as HTMLInputElement;
    const code = input.value.trim().toUpperCase();

    if (code.length !== 6) {
      this.showToast('Enter a 6-character room code', 'error');
      input.focus();
      return;
    }

    this.showScreen('connecting');
    document.getElementById('connecting-msg')!.textContent = `Joining ${code}…`;
    await this.joinRoom(code);
  }

  private async createRoom() {
    try {
      this.room = await this.client.create('game', {
        colorChoice: this.localColorChoice,
        emoji: this.localEmoji,
        difficulty: this.selectedDifficulty
      });

      // Expose connected state for testability
      const room = this.room;
      Object.defineProperty(room, 'connected', {
        get() { return room.connection.isOpen; },
        configurable: true,
      });

      // Update URL with room code so others can join via link
      window.history.replaceState({}, '', `?room=${this.room.id}`);

      this.setupRoomHandlers();
    } catch (error) {
      console.error('Failed to create room:', error);
      this.showScreen('landing');
      this.showToast('Server unavailable. Please try again in a moment.', 'error');
    }
  }

  private async joinRoom(roomCode: string) {
    try {
      this.room = await this.client.joinById(roomCode, {
        colorChoice: this.localColorChoice,
        emoji: this.localEmoji
      });

      // Expose connected state for testability
      const room = this.room;
      Object.defineProperty(room, 'connected', {
        get() { return room.connection.isOpen; },
        configurable: true,
      });

      if (!window.location.search.includes(roomCode)) {
        window.history.replaceState({}, '', `?room=${roomCode}`);
      }

      this.setupRoomHandlers();
    } catch (error: any) {
      console.error('Failed to join room:', error);
      const msg = this.parseJoinError(error, roomCode);
      this.showScreen('landing');
      this.showToast(msg, 'error');
      window.history.replaceState({}, '', '/');
    }
  }

  private parseJoinError(error: any, roomCode: string): string {
    const msg = (error?.message || '').toLowerCase();
    if (msg.includes('not found') || msg.includes('does not exist') || msg.includes('invalid')) {
      return `Room not found: "${roomCode}". Check the code and try again.`;
    }
    if (msg.includes('full') || msg.includes('max clients')) {
      return `Room "${roomCode}" is full (2/2 players).`;
    }
    return `Could not join room "${roomCode}". Check the code and try again.`;
  }

  // ── Room handlers ──────────────────────────────────────────────

  private setupRoomHandlers() {
    if (!this.room) return;

    this.room.onMessage('assignment', (data: any) => {
      this.localPlayerId = data.playerId;
      this.isHost = data.isHost;
      this.showLobby();
    });

    this.room.onMessage('player_count', (data: any) => {
      this.updateLobbyStatus(data.count, data.max);
    });

    this.room.onMessage('player_info', (data: any) => {
      this.updatePlayerCards(data.players);
    });

    this.room.onMessage('match_start', () => {
      this.resetRematchState();
      this.showScreen('hud');
      this.startGame();
    });

    this.room.onMessage('timer_update', (data: any) => {
      this.updateTimer(data.timeRemaining);
    });

    this.room.onMessage('territory_update', (data: any) => {
      this.updateScore(data.playerId, data.territory);
    });

    this.room.onMessage('player_death', (data: any) => {
      if (window.gameScene) window.gameScene.onPlayerDeath(data.playerId);
    });

    this.room.onMessage('player_respawn', (data: any) => {
      if (window.gameScene) window.gameScene.onPlayerRespawn(data.playerId);
    });

    this.room.onMessage('match_end', (data: any) => {
      // Check if player won on Warlord difficulty to unlock Anarchy
      if (data.difficulty === Difficulty.WARLORD) {
        const playerWon = (this.localPlayerId === 0 && data.player1Won) || 
                          (this.localPlayerId === 1 && data.player2Won);
        if (playerWon) {
          this.unlockAnarchy();
        }
      }
      this.showPostMatch(data.winner, data.score1, data.score2);
    });

    this.room.onMessage('post_match_state', (data: any) => {
      this.showPostMatch(data.winner, data.score1, data.score2);
    });

    this.room.onMessage('rematch_status', (data: any) => {
      this.rematchP1Ready = data.p1Ready;
      this.rematchP2Ready = data.p2Ready;
      this.updateRematchStatus();
    });

    this.room.onMessage('opponent_disconnected', () => {
      this.showToast('Opponent disconnected', 'warning');
    });

    this.room.onMessage('host_left', (data: any) => {
      this.showToast(data.message || 'Host left — room closed', 'warning');
      this.showScreen('landing');
      window.history.replaceState({}, '', '/');
      this.room = undefined;
    });

    this.room.onMessage('difficulty_set', (data: any) => {
      // Update local difficulty to match room (for joining players)
      if (data.difficulty && Object.values(Difficulty).includes(data.difficulty)) {
        this.selectedDifficulty = data.difficulty;
      }
    });

    this.room.onMessage('void_pulse_warning', (data: any) => {
      // Show void pulse warning effect in game scene
      if (window.gameScene) {
        (window.gameScene as any).showVoidPulseWarning?.(data.x, data.y, data.radius);
      }
    });

    this.room.onMessage('void_pulse_exploded', (data: any) => {
      // Show void pulse explosion effect in game scene
      if (window.gameScene) {
        (window.gameScene as any).showVoidPulseExplosion?.(data.x, data.y, data.radius);
      }
    });

    this.room.onStateChange((state: any) => {
      if (window.gameScene) window.gameScene.onStateChange(state);
    });

    this.room.onLeave((code) => {
      if (this.currentScreen !== 'landing' && code !== 1000) {
        this.showToast('Disconnected from room', 'warning');
      }
    });
  }

  // ── Lobby UI ───────────────────────────────────────────────────

  private showLobby() {
    this.showScreen('lobby');
    document.getElementById('lobby-room-code')!.textContent = this.room?.id || '';

    const startBtn = document.getElementById('start-match-btn') as HTMLButtonElement;
    if (!this.isHost) {
      startBtn.textContent = 'Waiting for host to start…';
    }

    this.updateMyPlayerCard();
    this.applyCustomizationToUI();
  }

  private updateMyPlayerCard() {
    const cardEl = document.getElementById(`player-card-${this.localPlayerId}`);
    if (!cardEl) return;

    cardEl.classList.remove('waiting');
    cardEl.classList.add('you');

    const emojiEl = cardEl.querySelector('.player-emoji-display') as HTMLElement;
    const tagEl = cardEl.querySelector('.player-card-tag') as HTMLElement;
    const swatchEl = document.getElementById(`swatch-${this.localPlayerId}`) as HTMLElement;

    if (emojiEl) emojiEl.textContent = this.localEmoji;
    if (tagEl) tagEl.textContent = '(You)';
    if (swatchEl) swatchEl.style.background = PLAYER_COLORS[this.localColorChoice];
  }

  private updatePlayerCards(players: Array<{ id: number; colorChoice: number; color: number; emoji: string }>) {
    for (const p of players) {
      const cardEl = document.getElementById(`player-card-${p.id}`);
      if (!cardEl) continue;

      const isMe = p.id === this.localPlayerId;
      cardEl.classList.remove('waiting');
      if (isMe) cardEl.classList.add('you');

      const emojiEl = cardEl.querySelector('.player-emoji-display') as HTMLElement;
      const tagEl = cardEl.querySelector('.player-card-tag') as HTMLElement;
      const swatchEl = document.getElementById(`swatch-${p.id}`) as HTMLElement;

      if (emojiEl) emojiEl.textContent = p.emoji;
      if (tagEl) tagEl.textContent = isMe ? '(You)' : '';
      if (swatchEl) swatchEl.style.background = PLAYER_COLORS[p.colorChoice] || `#${p.color.toString(16)}`;
    }

    this.updateHudColors(players);
  }

  private updateHudColors(players: Array<{ id: number; colorChoice: number; emoji: string }>) {
    for (const p of players) {
      const dotEl = document.getElementById(`hud-p${p.id + 1}-dot`) as HTMLElement;
      const labelEl = document.getElementById(`hud-p${p.id + 1}-label`) as HTMLElement;
      if (dotEl) dotEl.style.background = PLAYER_COLORS[p.colorChoice] || dotEl.style.background;
      if (labelEl) labelEl.textContent = `${p.emoji} P${p.id + 1}`;
    }
  }

  private updateLobbyStatus(count: number, max: number) {
    const statusEl = document.getElementById('lobby-status')!;
    const startBtn = document.getElementById('start-match-btn') as HTMLButtonElement;

    if (count >= max) {
      statusEl.innerHTML = '<span class="dot ready"></span>Both players connected — ready to start!';
      if (this.isHost) {
        startBtn.disabled = false;
        startBtn.textContent = 'Start Match';
      }
    } else {
      statusEl.innerHTML = `<span class="dot"></span>Waiting for opponent… (${count}/${max})`;
      if (this.isHost) startBtn.disabled = true;
    }

    for (let i = count; i < max; i++) {
      const card = document.getElementById(`player-card-${i}`);
      if (card && !card.classList.contains('you')) {
        card.classList.add('waiting');
        const emojiEl = card.querySelector('.player-emoji-display') as HTMLElement;
        const tagEl = card.querySelector('.player-card-tag') as HTMLElement;
        if (emojiEl) emojiEl.textContent = '?';
        if (tagEl) tagEl.textContent = 'Waiting…';
      }
    }
  }

  private copyInviteLink() {
    const code = this.room?.id || '';
    const link = `${window.location.origin}/?room=${code}`;
    const btn = document.getElementById('copy-code-btn') as HTMLButtonElement;

    navigator.clipboard.writeText(link).then(() => {
      btn.textContent = 'Copied!';
      btn.classList.add('copied');
      setTimeout(() => {
        btn.textContent = 'Copy link';
        btn.classList.remove('copied');
      }, 2000);
    }).catch(() => {
      this.showToast(`Share this code: ${code}`, 'info');
    });
  }

  // ── In-game UI ─────────────────────────────────────────────────

  private startGame() {
    if (window.gameScene) {
      window.gameScene.resetScene();
      window.gameScene.startGameplay();
    }
  }

  private updateTimer(seconds: number) {
    const timerEl = document.getElementById('timer')!;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    timerEl.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
    timerEl.classList.toggle('urgent', seconds <= 30);
  }

  private updateScore(playerId: number, territory: number) {
    const scoreEl = document.getElementById(playerId === 0 ? 'score-p1' : 'score-p2');
    if (scoreEl) scoreEl.textContent = territory.toString();
  }

  // ── Post-match UI ──────────────────────────────────────────────

  private showPostMatch(winner: string, score1: number, score2: number) {
    this.showScreen('post-match');

    const badge = document.getElementById('winner-badge')!;
    badge.className = 'winner-badge';
    if (winner === 'Draw') {
      badge.textContent = "It's a Draw!";
      badge.classList.add('draw');
    } else if (winner === 'Player 1') {
      badge.textContent = 'Player 1 Wins!';
      badge.classList.add('player1');
    } else {
      badge.textContent = 'Player 2 Wins!';
      badge.classList.add('player2');
    }

    document.getElementById('final-score-p1')!.textContent = String(score1);
    document.getElementById('final-score-p2')!.textContent = String(score2);

    const playBtn = document.getElementById('play-again-btn') as HTMLButtonElement;
    playBtn.disabled = false;
    playBtn.textContent = 'Play Again';
    this.hasClickedRematch = false;
    this.updateRematchStatus();
  }

  private onPlayAgain() {
    if (!this.room || this.hasClickedRematch) return;
    this.hasClickedRematch = true;
    const playBtn = document.getElementById('play-again-btn') as HTMLButtonElement;
    playBtn.disabled = true;
    playBtn.textContent = 'Waiting for opponent…';
    this.room.send('rematch_ready', {});
  }

  private resetRematchState() {
    this.rematchP1Ready = false;
    this.rematchP2Ready = false;
    this.hasClickedRematch = false;
    document.getElementById('score-p1')!.textContent = '0';
    document.getElementById('score-p2')!.textContent = '0';
    document.getElementById('timer')!.textContent = '3:00';
    document.getElementById('timer')!.classList.remove('urgent');
  }

  private updateRematchStatus() {
    const statusEl = document.getElementById('rematch-status')!;
    const p1Label = this.localPlayerId === 0 ? 'You' : 'P1';
    const p2Label = this.localPlayerId === 1 ? 'You' : 'P2';
    const parts = [
      this.rematchP1Ready
        ? `<span class="ready-indicator"><span class="check">✓</span> ${p1Label} ready</span>`
        : `${p1Label} not ready`,
      this.rematchP2Ready
        ? `<span class="ready-indicator"><span class="check">✓</span> ${p2Label} ready</span>`
        : `${p2Label} not ready`
    ];
    statusEl.innerHTML = parts.join(' &nbsp;·&nbsp; ');
  }

  // ── Screen management ──────────────────────────────────────────

  private showScreen(screen: Screen) {
    this.currentScreen = screen;

    const screenIds = ['landing-screen', 'connecting-screen', 'lobby-screen', 'post-match-screen', 'hud-overlay', 'difficulty-select-screen'];
    screenIds.forEach(id => document.getElementById(id)?.classList.add('hidden'));

    const idMap: Record<Screen, string> = {
      'landing': 'landing-screen',
      'connecting': 'connecting-screen',
      'lobby': 'lobby-screen',
      'hud': 'hud-overlay',
      'post-match': 'post-match-screen',
      'attract': 'landing-screen', // Attract mode uses landing screen as base
      'difficulty-select': 'difficulty-select-screen'
    };
    document.getElementById(idMap[screen])?.classList.remove('hidden');
  }

  // ── Toast ──────────────────────────────────────────────────────

  private showToast(message: string, type: 'error' | 'info' | 'warning' | 'success' = 'info') {
    const container = document.getElementById('toast-container')!;
    const toast = document.createElement('div');
    toast.className = `toast ${type}`;
    toast.textContent = message;
    container.appendChild(toast);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => toast.classList.add('show'));
    });

    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 250);
    }, 4000);
  }

  // ── Phaser init ────────────────────────────────────────────────

  private initPhaser() {
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: 1200,
      height: 880,
      parent: 'game-wrap',
      backgroundColor: '#07091a',
      scene: [GameScene],
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH
      }
    };

    window.gameInstance = new Phaser.Game(config);
    window.gameClient = this;
  }

  // ── Input forwarding ──────────────────────────────────────────

  sendInput(drawMode: boolean, direction: string | null) {
    if (this.room) {
      this.room.send('input', { drawMode, direction });
    }
  }
}

declare global {
  interface Window {
    gameInstance?: Phaser.Game;
    gameScene?: GameScene;
    gameClient?: LinkLockGame;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new LinkLockGame());
} else {
  new LinkLockGame();
}

export default LinkLockGame;

// ── Bot AI for Attract Mode ────────────────────────────────────────

interface BotState {
  x: number;
  y: number;
  color: number;
  trail: { x: number; y: number }[];
  territory: number;
  alive: boolean;
  drawMode: boolean;
}

class BotAI {
  public x: number;
  public y: number;
  public spawnX: number;
  public spawnY: number;
  public color: number;
  public trail: { x: number; y: number }[] = [];
  public territory: number = 0;
  public isAlive: boolean = true;
  public drawMode: boolean = false;

  private playerId: number;
  private grid: number[][] = [];
  private lastActionTime: number = 0;
  private targetDirection: string | null = null;

  // Directions for 8-way movement
  private directions = ['up', 'down', 'left', 'right', 'upleft', 'upright', 'downleft', 'downright'];

  constructor(playerId: number, color: number) {
    this.playerId = playerId;
    this.color = color;
    this.spawnX = playerId === 0 ? 2 : GRID_WIDTH - 3;
    this.spawnY = playerId === 0 ? 2 : GRID_HEIGHT - 3;
    this.x = this.spawnX;
    this.y = this.spawnY;
    this.initializeGrid();
  }

  private initializeGrid() {
    for (let y = 0; y < GRID_HEIGHT; y++) {
      const row: number[] = [];
      for (let x = 0; x < GRID_WIDTH; x++) {
        row.push(0); // EMPTY
      }
      this.grid.push(row);
    }
  }

  update(opponent: BotAI) {
    if (!this.isAlive) return;

    const now = Date.now();
    
    // Apply reaction delay
    if (now - this.lastActionTime < ATTRACT_BOT_DELAY) return;

    // 15% chance to "miss" (make a random move instead of optimal)
    const shouldMiss = Math.random() < ATTRACT_MISS_RATE;

    // Decide whether to draw
    if (!this.drawMode && this.trail.length === 0) {
      // Start drawing occasionally
      if (Math.random() < 0.1) {
        this.drawMode = true;
      }
    }

    // Stop drawing after some time
    if (this.drawMode && this.trail.length > 10 && Math.random() < 0.05) {
      this.drawMode = false;
      this.clearTrail();
    }

    // Find target direction
    this.targetDirection = this.findBestDirection(opponent, shouldMiss);

    // Move
    if (this.targetDirection) {
      this.move(this.targetDirection);
    }

    // Handle trail on grid
    if (this.drawMode) {
      this.trail.push({ x: this.x, y: this.y });
      this.grid[this.y][this.x] = this.playerId === 0 ? 3 : 4; // TRAIL_P1 or TRAIL_P2

      // Check for loop closed
      if (this.checkLoopClosed()) {
        this.claimTerritory();
      }
    }

    // Check boundary and collision
    this.checkCollisions(opponent);

    this.lastActionTime = now;
  }

  private findBestDirection(opponent: BotAI, shouldMiss: boolean): string | null {
    // Get valid moves
    const validMoves = this.getValidMoves();

    if (validMoves.length === 0) return null;

    if (shouldMiss) {
      // Random move for "miss"
      return validMoves[Math.floor(Math.random() * validMoves.length)];
    }

    // Strategy: try to close loops and claim territory
    // Look for positions that could lead to loops
    let bestScore = -Infinity;
    let bestMove = validMoves[0];

    for (const dir of validMoves) {
      const newPos = this.getNextPosition(dir);
      let score = 0;

      // Prefer moves that stay away from edges
      if (newPos.x > 2 && newPos.x < GRID_WIDTH - 3 && newPos.y > 2 && newPos.y < GRID_HEIGHT - 3) {
        score += 10;
      }

      // Prefer drawing trails that could form loops
      if (this.drawMode) {
        score += 5;
        
        // Check if closing a loop is possible
        for (const trailPoint of this.trail) {
          const dx = Math.abs(newPos.x - trailPoint.x);
          const dy = Math.abs(newPos.y - trailPoint.y);
          if (dx <= 1 && dy <= 1 && (dx + dy > 0)) {
            score += 30; // High score for potential loop closure
          }
        }
      }

      // Avoid opponent trail
      if (opponent && opponent.trail.some(t => t.x === newPos.x && t.y === newPos.y)) {
        score -= 100;
      }

      // Avoid own trail
      if (this.trail.some(t => t.x === newPos.x && t.y === newPos.y)) {
        score -= 50;
      }

      if (score > bestScore) {
        bestScore = score;
        bestMove = dir;
      }
    }

    return bestMove;
  }

  private getValidMoves(): string[] {
    const valid: string[] = [];
    
    for (const dir of this.directions) {
      const pos = this.getNextPosition(dir);
      if (pos.x >= 0 && pos.x < GRID_WIDTH && pos.y >= 0 && pos.y < GRID_HEIGHT) {
        valid.push(dir);
      }
    }
    
    return valid;
  }

  private getNextPosition(dir: string): { x: number; y: number } {
    let newX = this.x;
    let newY = this.y;
    
    switch (dir) {
      case 'up': newY--; break;
      case 'down': newY++; break;
      case 'left': newX--; break;
      case 'right': newX++; break;
      case 'upleft': newX--; newY--; break;
      case 'upright': newX++; newY--; break;
      case 'downleft': newX--; newY++; break;
      case 'downright': newX++; newY++; break;
    }
    
    return { x: newX, y: newY };
  }

  private move(dir: string) {
    const pos = this.getNextPosition(dir);
    this.x = pos.x;
    this.y = pos.y;
  }

  private checkLoopClosed(): boolean {
    if (this.trail.length < 4) return false;

    const current = this.trail[this.trail.length - 1];

    for (let i = 0; i < this.trail.length - 3; i++) {
      const point = this.trail[i];
      const dx = Math.abs(current.x - point.x);
      const dy = Math.abs(current.y - point.y);

      if (dx <= 1 && dy <= 1 && (dx + dy > 0)) {
        return true;
      }
    }

    return false;
  }

  private claimTerritory() {
    // Simple flood fill to claim territory
    const claimed = this.floodFillTerritory();
    this.territory += claimed;
    this.clearTrail();
  }

  private floodFillTerritory(): number {
    // Find enclosed area (simplified)
    // Mark trail cells as territory
    let claimed = 0;
    const myTrail = this.playerId === 0 ? 1 : 2; // TERRITORY_P1 or TERRITORY_P2

    for (const pos of this.trail) {
      if (this.grid[pos.y][pos.x] === 0) {
        this.grid[pos.y][pos.x] = myTrail;
        claimed++;
      }
    }

    // Claim a few random nearby cells to simulate territory expansion
    for (let i = 0; i < 5; i++) {
      const dx = Math.floor(Math.random() * 5) - 2;
      const dy = Math.floor(Math.random() * 5) - 2;
      const tx = this.x + dx;
      const ty = this.y + dy;
      
      if (tx >= 0 && tx < GRID_WIDTH && ty >= 0 && ty < GRID_HEIGHT && this.grid[ty][tx] === 0) {
        this.grid[ty][tx] = myTrail;
        claimed++;
      }
    }

    return claimed;
  }

  private clearTrail() {
    for (const pos of this.trail) {
      if (this.grid[pos.y][pos.x] === 3 || this.grid[pos.y][pos.x] === 4) {
        this.grid[pos.y][pos.x] = 0;
      }
    }
    this.trail = [];
  }

  private checkCollisions(opponent: BotAI) {
    // Check boundary
    if (this.x < 0 || this.x >= GRID_WIDTH || this.y < 0 || this.y >= GRID_HEIGHT) {
      this.die();
      return;
    }

    // Check own trail collision
    for (const pos of this.trail) {
      if (this.x === pos.x && this.y === pos.y) {
        this.die();
        return;
      }
    }

    // Check opponent trail collision
    if (opponent) {
      for (const pos of opponent.trail) {
        if (this.x === pos.x && this.y === pos.y) {
          this.die();
          return;
        }
      }

      // Head-to-head
      if (this.x === opponent.x && this.y === opponent.y) {
        this.die();
        opponent.die();
      }
    }
  }

  private die() {
    this.isAlive = false;
    this.clearTrail();
    this.x = this.spawnX;
    this.y = this.spawnY;
    
    // Respawn after delay
    setTimeout(() => {
      this.isAlive = true;
    }, 1000);
  }

  getState(): BotState {
    return {
      x: this.x,
      y: this.y,
      color: this.color,
      trail: [...this.trail],
      territory: this.territory,
      alive: this.isAlive,
      drawMode: this.drawMode
    };
  }

  getGrid(): number[][] {
    return this.grid;
  }
}
